import { useState, useCallback, useRef, useEffect, useMemo, useDeferredValue } from "react";
import { ToolContainer, FileDropZone, DownloadButton, downloadBlob, ResultPreview } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useFreemium } from "@/hooks/useFreemium";
import { loadFFmpeg, fetchFile, checkFileSize, ffmpegOutputToBlob, resetFFmpegEngine } from "@/lib/ffmpegEngine";
import { measureNoiseFloorDb } from "./noise";
import { convertAudioFormat, stepOutputFormat, formatDuration, formatMinSecTenth } from "./operations";
import { removeSilence as cutSilence, encodeWav, detectPauses, predictedSeconds, describeSilenceResult, rateKeepingCodec } from "./silence";
import { decodeAudioFile, type DecodedAudio } from "./audioFile";
import { AudioProcessingProgress, showAudioError, FileSizeNotice, useAudioPipelineStep, UNREADABLE_AUDIO_MESSAGE } from "./uiHelpers";
import type { FFmpeg } from "@ffmpeg/ffmpeg";

const TOOL_INFO_CONTENT = {
  what: `Automatically detects and trims quiet or silent sections from an audio file — useful for cleaning up a voice recording with long dead air, or tightening up a podcast with awkward pauses. Runs entirely in your browser — the audio never leaves your device.`,
  how: `Upload your audio file and adjust the silence threshold and minimum duration if needed. Every pause that would be cut is listed with its time range: un-tick any you want to keep (a dramatic pause, a beat before a punchline). Preview the result right on the page, then Remove Silence and download.`,
  tip: `This trims silence throughout the file, not just at the start and end — it looks for any stretch quieter than the threshold and at least as long as the minimum duration, and removes it. If it's cutting out parts that shouldn't be silent, try lowering the threshold (a more negative dB value is stricter about what counts as "silent").`,
};

export default function SilenceRemover() {
  const { inPipeline, intermediate } = useAudioPipelineStep();
  const [file, setFile] = useState<File | null>(null);
  const [thresholdDb, setThresholdDb] = useState([-35]);
  const [minDuration, setMinDuration] = useState([0.5]);
  const [keepPause, setKeepPause] = useState(inPipeline ? 0.5 : 0); // inside a bundle, speech sounds natural with short pauses kept
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [result, setResult] = useState<{ blob: Blob; size: number; summary: string } | null>(null);
  const [decoded, setDecoded] = useState<DecodedAudio | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [leave, setLeave] = useState<Set<number>>(new Set());
  const [preview, setPreview] = useState<Blob | null>(null);
  const { isPro, isLoading, isAdmin } = useFreemium();
  const ffmpegRef = useRef<FFmpeg | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const handleFiles = useCallback((files: File[]) => {
    const f = files[0]; if (!f) return;
    setFile(f); setResult(null); setDecoded(null); setLoadFailed(false); setLeave(new Set()); setPreview(null);
  }, []);

  // The file is decoded as soon as it is chosen: the pauses are found on the decoded audio, so they can be listed and previewed before anything is processed.
  useEffect(() => {
    if (!file) return;
    let live = true;
    decodeAudioFile(file, { maxChannels: 2, matchFileRate: true }).then((d) => { if (live) setDecoded(d); }, () => { if (live) setLoadFailed(true); });
    return () => { live = false; };
  }, [file]);

  // Dragging a slider fires many times a second; the pause search follows the settled value.
  const params = useDeferredValue({ t: thresholdDb[0], m: minDuration[0], k: keepPause });
  const pauses = useMemo(
    () => (decoded ? detectPauses(decoded.channels, decoded.sampleRate, params.t, params.m, params.k) : []),
    [decoded, params.t, params.m, params.k]
  );
  useEffect(() => { setLeave(new Set()); setPreview(null); }, [params.t, params.m, params.k]);

  const total = decoded?.channels[0]?.length ?? 0;
  const cuttable = pauses.filter((p) => p.cut);
  const ticked = cuttable.filter((p) => !leave.has(p.start));
  const predicted = decoded ? predictedSeconds(pauses, leave, total, decoded.sampleRate) : 0;
  const togglePause = (start: number) => {
    setLeave((prev) => { const next = new Set(prev); if (next.has(start)) next.delete(start); else next.add(start); return next; });
    setResult(null); setPreview(null);
  };
  const setAll = (cut: boolean) => { setLeave(cut ? new Set() : new Set(cuttable.map((p) => p.start))); setResult(null); setPreview(null); };
  const previewTooBig = decoded ? total * decoded.channels.length * 2 > 150 * 1024 * 1024 : false;

  const sizeCheck = file ? checkFileSize([file], isPro || isLoading, isAdmin) : null;

  const stop = () => {
    abortRef.current?.abort();
    if (ffmpegRef.current) resetFFmpegEngine(ffmpegRef.current);
  };

  const autoThreshold = () => {
    if (!decoded) return;
    const floor = measureNoiseFloorDb(decoded.channels.slice(0, 2), decoded.sampleRate);
    const t = Math.max(-60, Math.min(-15, Math.round((floor + 10) / 5) * 5));
    setThresholdDb([t]); setResult(null);
    toast.success(`Background noise is about ${Math.round(floor)} dB, so the threshold is set to ${t} dB.`);
  };

  /** Plays the result straight away, built in the browser: nothing is processed, and no trial use is taken. */
  const makePreview = () => {
    if (!decoded) return;
    const cut = cutSilence(decoded.channels, decoded.sampleRate, params.t, params.m, params.k, leave);
    setPreview(new Blob([encodeWav(cut.channels, decoded.sampleRate) as BlobPart], { type: "audio/wav" }));
  };

  const removeSilence = async () => {
    if (!file || !decoded) return;
    setProcessing(true);
    setProgress(0);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setStatusMessage("Loading audio engine...");
      const ffmpeg = await loadFFmpeg((msg) => setStatusMessage(msg));
      ffmpegRef.current = ffmpeg;

      setStatusMessage("Removing silence...");
      // Silence is found and cut here, on the decoded audio (see silence.ts); ffmpeg only encodes the result.
      const cut = cutSilence(decoded.channels, decoded.sampleRate, params.t, params.m, params.k, leave);
      if (cut.keptSeconds < 0.1) {
        toast.info("The whole file is quieter than the threshold. Try a lower (more negative) threshold.");
        return;
      }

      setStatusMessage("Saving...");
      await ffmpeg.writeFile("cut.wav", encodeWav(cut.channels, decoded.sampleRate));
      const fmtOut = stepOutputFormat(file, intermediate);
      // Same sample rate out as in: the decoder was asked for the file's own rate; if this browser decoded at another one, convert back.
      const outRate = decoded.fileRate ?? decoded.sampleRate;
      const data = await convertAudioFormat(ffmpeg, "cut.wav", `output.${fmtOut.value}`, rateKeepingCodec(fmtOut.codec, decoded.sampleRate, decoded.fileRate), setProgress, controller.signal);
      const summary = `${describeSilenceResult({ beforeSeconds: decoded.duration, afterSeconds: cut.keptSeconds, cut: cut.pausesRemoved, kept: cuttable.length - cut.pausesRemoved, keepPauseSeconds: params.k })}. Saved at ${+(outRate / 1000).toFixed(2)} kHz${decoded.fileRate ? ", the same as your file" : ""}.`;

      const blob = ffmpegOutputToBlob(data, fmtOut.mime);
      setResult({ blob, size: blob.size, summary });
      toast.success("Silence removed!");
    } catch (err) {
      showAudioError(err);
    } finally {
      if (ffmpegRef.current) resetFFmpegEngine(ffmpegRef.current);
      setProcessing(false);
    }
  };

  const reset = () => { setFile(null); setResult(null); setDecoded(null); setLoadFailed(false); setThresholdDb([-35]); setMinDuration([0.5]); setLeave(new Set()); setPreview(null); };

  return (
    <ToolContainer>
      {!file ? (
        <FileDropZone gateAtDownload accept="audio/*" maxSize={500} onFiles={handleFiles} label="Drop an audio file" description="Automatically trim silent sections" />
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border bg-card flex justify-between">
            <div><p className="text-sm font-medium">{file.name}</p><p className="text-xs text-muted-foreground">Original: {(file.size/1024/1024).toFixed(1)} MB</p></div>
            {result && <p className="text-sm font-medium text-green-600">{(result.size/1024/1024).toFixed(1)} MB</p>}
          </div>
          {sizeCheck && <FileSizeNotice check={sizeCheck} isPro={isPro} />}
          {loadFailed && (
            <div role="alert" className="p-3 rounded-lg border border-destructive/50 text-sm text-destructive flex items-center justify-between gap-3">
              <span>{UNREADABLE_AUDIO_MESSAGE}</span>
              <Button size="sm" variant="outline" onClick={reset}>Choose another</Button>
            </div>
          )}

          <div className="space-y-3 max-w-sm">
            <div>
              <Label>Silence Threshold: {thresholdDb[0]} dB</Label>
              <Slider aria-label="Silence threshold (dB)" value={thresholdDb} onValueChange={(v) => { setThresholdDb(v); setResult(null); }} min={-60} max={-15} step={5} />
            </div>
            <div className="flex flex-wrap gap-2 items-center text-xs">
              <Button size="sm" variant="outline" className="h-7" onClick={autoThreshold} disabled={!decoded}>Auto threshold</Button>
              <span className="text-muted-foreground">sets the threshold from this file's own background noise</span>
            </div>
            <div>
              <Label>Pauses</Label>
              <select className="block border rounded px-2 py-1 w-full bg-background text-sm" value={keepPause} onChange={(e) => { setKeepPause(Number(e.target.value)); setResult(null); }} aria-label="What to do with pauses">
                <option value={0}>Remove them completely</option>
                <option value={0.3}>Shorten to about 0.3 s</option>
                <option value={0.5}>Shorten to about 0.5 s</option>
                <option value={1}>Shorten to about 1 s</option>
              </select>
            </div>
            <div>
              <Label>Minimum Duration: {minDuration[0]}s</Label>
              <Slider aria-label="Minimum silence duration (seconds)" value={minDuration} onValueChange={(v) => { setMinDuration(v); setResult(null); }} min={0.2} max={2} step={0.1} />
            </div>
          </div>

          {decoded && (
            <div className="space-y-2" data-testid="pause-list">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-medium">
                  {pauses.length === 0 ? "No pauses found at these settings" : `${pauses.length} ${pauses.length === 1 ? "pause" : "pauses"} found`}
                </p>
                {cuttable.length > 1 && (
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setAll(true)}>Cut all</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setAll(false)}>Keep all</Button>
                  </div>
                )}
              </div>
              {pauses.length > 0 && (
                <ul className="max-h-60 overflow-auto rounded-lg border divide-y text-sm">
                  {pauses.slice(0, 1000).map((p) => {
                    const id = `pause-${p.start}`;
                    const on = !!p.cut && !leave.has(p.start);
                    return (
                      <li key={p.start} className="flex items-center gap-3 px-3 py-1.5">
                        <input id={id} type="checkbox" className="h-4 w-4" checked={on} disabled={!p.cut} onChange={() => togglePause(p.start)} />
                        <label htmlFor={id} className="flex-1 cursor-pointer font-mono text-xs">{formatMinSecTenth(p.startSeconds)}–{formatMinSecTenth(p.endSeconds)} <span className="text-muted-foreground font-sans">({p.durationSeconds.toFixed(1)} s{p.cut ? "" : ", already short enough, left as it is"})</span></label>
                      </li>
                    );
                  })}
                </ul>
              )}
              {pauses.length > 1000 && <p className="text-xs text-muted-foreground">Showing the first 1000 pauses; Cut all and Keep all cover every pause.</p>}
              {pauses.length > 0 && <p className="text-xs text-muted-foreground" data-testid="pause-prediction">Ticked pauses are cut: {formatDuration(decoded.duration)} → {formatDuration(predicted)} ({ticked.length} of {cuttable.length} cut). Un-tick a pause to keep it.</p>}
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="outline" onClick={makePreview} disabled={previewTooBig || pauses.length === 0}>Preview the result</Button>
                <span className="text-xs text-muted-foreground">{previewTooBig ? "Too long to preview here; Remove Silence makes the file and you can listen to it before downloading." : "Plays here, costs nothing."}</span>
              </div>
              {preview && <ResultPreview blob={preview} />}
            </div>
          )}

          {processing && <AudioProcessingProgress progress={progress} statusMessage={statusMessage} onStop={stop} />}
          {result && <p className="text-sm text-muted-foreground" data-testid="silence-summary">{result.summary}</p>}
          {result && <ResultPreview blob={result.blob} />}
          <div className="flex gap-3">
            {result ? (
              <>
                <DownloadButton blob={result.blob} fileName={`desilenced_${file.name.replace(/\.[^.]+$/, `.${stepOutputFormat(file, intermediate).value}`)}`} />
                <Button variant="outline" onClick={reset}>New</Button>
              </>
            ) : (
              <>
                <Button onClick={removeSilence} disabled={processing || sizeCheck?.severity !== "ok" || !decoded || ticked.length === 0}>{processing ? "Removing..." : "Remove Silence"}</Button>
                <Button variant="outline" onClick={reset} disabled={processing}>Reset</Button>
              </>
            )}
          </div>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
